import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

export default function BarcodeScannerDialog({
  open,
  onClose,
  onScan,
  title = "Escanear código",
  helper = "Escaneá con la lectora USB. También podés escribir o pegar el código y presionar Enter.",
  submitLabel = "Buscar",
}) {
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState("");
  const [processing, setProcessing] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    setCode("");
    setError("");
    setProcessing(false);
    const timer = window.setTimeout(() => inputRef.current?.focus?.(), 80);
    return () => window.clearTimeout(timer);
  }, [open]);

  const submit = React.useCallback(async () => {
    const value = String(code || "").trim();
    if (!value || processing) return;
    try {
      setProcessing(true);
      setError("");
      await onScan?.(value);
      onClose?.();
    } catch (err) {
      setError(err?.message || "No se pudo buscar el código.");
      window.setTimeout(() => inputRef.current?.focus?.(), 0);
    } finally {
      setProcessing(false);
    }
  }, [code, onClose, onScan, processing]);

  return (
    <Dialog open={open} onClose={processing ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <QrCodeScannerRoundedIcon />
          <span>{title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Lectora lista
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {helper}
            </Typography>
          </Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            inputRef={inputRef}
            autoFocus
            fullWidth
            label="Código leído"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submit();
              }
            }}
            disabled={processing}
            autoComplete="off"
            inputProps={{ spellCheck: false }}
            helperText="La mayoría de las lectoras USB escriben el código y envían Enter automáticamente."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={processing} sx={{ textTransform: "none", fontWeight: 800 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!String(code || "").trim() || processing}
          startIcon={<SearchRoundedIcon />}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
